import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { ArrowLeft, Plant, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const WPM = 200;

function readingTime(post) {
  const words = (post?.body_markdown ? post.body_markdown.trim().split(/\s+/).length : null) || post?.word_count || 0;
  if (!words) return null;
  const mins = Math.max(1, Math.round(words / WPM));
  return `${mins} min read`;
}

function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

// Markdown component overrides — module-scoped so react-markdown doesn't
// remount them on every parent render.
const MD_COMPONENTS = {
  h1: (p) => <h1 className="font-display text-4xl md:text-5xl text-ink mt-12 mb-4 leading-tight" {...p} />,
  h2: (p) => <h2 className="font-display text-3xl md:text-4xl text-ink mt-12 mb-4 leading-tight" {...p} />,
  h3: (p) => <h3 className="font-display text-2xl md:text-3xl text-ink mt-10 mb-3 leading-snug" {...p} />,
  h4: (p) => <h4 className="font-display text-xl text-ink mt-8 mb-2" {...p} />,
  p:  (p) => <p className="text-ink/90 leading-relaxed my-5" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 my-5 space-y-2 text-ink/90 marker:text-forest" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 my-5 space-y-2 text-ink/90 marker:text-forest" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  blockquote: (p) => <blockquote className="border-l-4 border-ochre pl-5 my-6 italic text-mute" {...p} />,
  strong: (p) => <strong className="font-semibold text-ink" {...p} />,
  em: (p) => <em className="italic text-terra" {...p} />,
  a: (p) => <a className="text-forest underline underline-offset-2 hover:text-forest-dark" {...p} />,
  hr: (p) => <hr className="my-8 border-sand-200" {...p} />,
  code: ({ inline, ...p }) => (
    inline
      ? <code className="px-1.5 py-0.5 rounded bg-sand-100 border border-sand-200 font-mono-data text-sm" {...p} />
      : <code className="block p-4 rounded-xl bg-sand-100 border border-sand-200 font-mono-data text-sm overflow-x-auto" {...p} />
  ),
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}`);
        setPost(data);
      } catch {
        toast.error("Post not found");
        navigate("/blog");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  // ------------------------------------------------------------------
  // SEO: inject canonical link + refresh title/description/og-image
  // whenever the post loads. Cleaned up on unmount / slug change.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!post) return undefined;

    const SITE = "https://amustforearth.com";
    const canonicalUrl = `${SITE}/blog/${post.slug}`;

    const prevTitle = document.title;
    document.title = `${post.title} — A Must For Earth`;

    const upsertMeta = (attr, name, content) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev, created };
    };

    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return { el, prev, created };
    };

    const canonical = upsertLink("canonical", canonicalUrl);
    const desc = upsertMeta("name", "description", post.excerpt || `A Must For Earth field note: ${post.title}`);
    const ogTitle = upsertMeta("property", "og:title", `${post.title} — A Must For Earth`);
    const ogUrl = upsertMeta("property", "og:url", canonicalUrl);
    const ogDesc = upsertMeta("property", "og:description", post.excerpt || post.title);
    const ogType = upsertMeta("property", "og:type", "article");
    const twTitle = upsertMeta("name", "twitter:title", post.title);
    const twDesc = upsertMeta("name", "twitter:description", post.excerpt || post.title);
    const ogImageVal = `${SITE}/api/blog/${post.slug}/og-card.png`;
    const ogImage = upsertMeta("property", "og:image", ogImageVal);
    const ogImageW = upsertMeta("property", "og:image:width", "1200");
    const ogImageH = upsertMeta("property", "og:image:height", "630");
    const twCard = upsertMeta("name", "twitter:card", "summary_large_image");
    const twImage = upsertMeta("name", "twitter:image", ogImageVal);

    return () => {
      document.title = prevTitle;
      const restore = ({ el, prev, created }) => {
        if (created) {
          el.parentNode && el.parentNode.removeChild(el);
        } else if (prev != null) {
          if (el.tagName === "LINK") el.setAttribute("href", prev);
          else el.setAttribute("content", prev);
        }
      };
      [canonical].forEach(restore);
      [desc, ogTitle, ogUrl, ogDesc, ogType, twTitle, twDesc, ogImage, ogImageW, ogImageH, twCard, twImage].forEach(restore);
    };
  }, [post]);

  // ------------------------------------------------------------------
  // Reading-progress bar: 0→100% based on how far through the article
  // the reader has scrolled. rAF-throttled for smoothness.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!post) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const doc = document.documentElement;
        const total = doc.scrollHeight - window.innerHeight;
        const y = window.scrollY || doc.scrollTop || 0;
        const pct = total > 0 ? Math.min(100, Math.max(0, (y / total) * 100)) : 0;
        setProgress(pct);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand-50">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-14 font-mono-data text-mute animate-pulse-soft">loading post…</main>
      </div>
    );
  }
  if (!post) return null;

  return (
    <div className="min-h-screen bg-sand-50" data-testid="blog-post-page">
      {/* Reading-progress bar — thin forest line that fills as the user scrolls */}
      <div
        aria-hidden
        className="fixed top-0 left-0 right-0 h-[3px] z-[60] pointer-events-none"
        data-testid="reading-progress"
      >
        <div
          className="h-full bg-gradient-to-r from-forest to-ochre transition-[width] duration-100 ease-out shadow-[0_0_8px_rgba(44,76,59,0.35)]"
          style={{ width: `${progress}%` }}
        />
      </div>
      <Navbar />
      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/blog" className="btn-secondary !text-sm" data-testid="back-to-blog">
          <ArrowLeft size={14} /> Back to blog
        </Link>

        <header className="mt-8">
          <div className="font-mono-data text-mute text-xs flex items-center gap-3 flex-wrap" data-testid="blog-post-meta">
            <span>{fmtDate(post.created_at)}</span>
            {readingTime(post) && (
              <>
                <span aria-hidden>·</span>
                <span className="inline-flex items-center gap-1" data-testid="blog-post-reading-time">
                  <Clock size={12} weight="duotone" /> {readingTime(post)}
                </span>
              </>
            )}
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-ink leading-tight mt-2">{post.title}</h1>
          {post.excerpt && (
            <p className="text-mute mt-4 text-lg leading-relaxed">{post.excerpt}</p>
          )}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {post.tags.map((t, i) => (
                <span key={i} className="tag severity-mild">{t}</span>
              ))}
            </div>
          )}
        </header>

        <div className="mt-8 rounded-3xl overflow-hidden bg-sand-100 border border-sand-200 aspect-[16/9]">
          {post.image_b64 ? (
            <img
              src={`data:image/png;base64,${post.image_b64}`}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-mute">
              <Plant size={48} weight="duotone" />
            </div>
          )}
        </div>

        <div className="mt-10 space-y-5 text-ink/90 leading-relaxed text-lg" data-testid="blog-body">
          <ReactMarkdown components={MD_COMPONENTS}>{post.body_markdown || ""}</ReactMarkdown>
        </div>

        <div className="mt-14 bg-sand-100 border border-sand-200 rounded-3xl p-8 text-center" data-testid="blog-cta">
          <h3 className="font-display text-3xl text-ink">Diagnose your own plant</h3>
          <p className="text-mute mt-2 max-w-md mx-auto">
            Snap a photo or short video. We fuse your local climate with GPT-5.5 vision and tell you what&apos;s wrong.
          </p>
          <Link to="/register" className="btn-primary mt-6 inline-flex">Start scanning</Link>
        </div>
      </article>
    </div>
  );
}
